import { indentsApi } from '../api/modules/indents.api';
import { getApiErrorMessage } from '../api/client';
import { USE_API } from '../config/api';
import { indentSeed } from '../data/master/indents';
import { mapPaginated, indentFromApi, indentToApi } from '../models';
import { getEquipment } from './equipment.repository';
import { downloadExcelResponse, exportFileStamp } from '../utils/exportExcel';

export async function listIndents(params = {}) {
  if (!USE_API) {
    let items = [...indentSeed];
    if (params.search) {
      const q = String(params.search).toLowerCase();
      items = items.filter(
        (i) =>
          i.indentId?.toLowerCase().includes(q) ||
          i.raisedByName?.toLowerCase().includes(q) ||
          i.vendorId?.toLowerCase().includes(q),
      );
    }
    if (params.status) {
      const s = String(params.status).toLowerCase();
      items = items.filter((i) => String(i.status).toLowerCase() === s);
    }
    const page = params.page || 1;
    const pageSize = params.pageSize || items.length || 10;
    const start = (page - 1) * pageSize;
    return {
      items: items.slice(start, start + pageSize),
      total: items.length,
      page,
      pageSize,
    };
  }
  try {
    const result = await indentsApi.list(params);
    return mapPaginated(result, indentFromApi);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to load indents'));
  }
}

export async function exportIndentsExcel(params = {}) {
  if (!USE_API) throw new Error('Export requires API mode');
  try {
    const response = await indentsApi.exportExcel(params);
    return downloadExcelResponse(
      response,
      `indent_requests_${exportFileStamp()}.xlsx`,
    );
  } catch (error) {
    throw new Error(error?.message || getApiErrorMessage(error, 'Export failed'));
  }
}

export async function getPendingIndentCount() {
  if (!USE_API) {
    const pending = indentSeed.filter(
      (i) => String(i.status || '').toLowerCase() === 'pending',
    ).length;
    return { pending };
  }
  try {
    const data = await indentsApi.pendingCount();
    return { pending: Number(data?.pending) || 0 };
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to load pending indent count'));
  }
}

export async function getIndent(id) {
  if (!USE_API) {
    return indentSeed.find((i) => i.indentId === id) || null;
  }
  try {
    return await enrichIndentEquipment(indentFromApi(await indentsApi.getById(id)));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to load indent'));
  }
}

/** Fill recommendation/assignment equipment if API omitted nested relations. */
async function enrichIndentEquipment(indent) {
  if (!indent) return null;
  const ids = new Set();
  const collect = (r) => {
    // Member fleet recommendations use userEquipmentId — not equipment_assets.
    if (r.userEquipmentId != null || r.targetType === 'user_equipment') return;
    if (!r.equipment && r.equipmentId != null) ids.add(Number(r.equipmentId));
  };
  (indent.lines || []).forEach((line) => {
    (line.recommendations || []).forEach(collect);
    if (
      line.assignment &&
      !line.assignment.equipment &&
      line.assignment.equipmentId != null &&
      line.assignment.userEquipmentId == null
    ) {
      ids.add(Number(line.assignment.equipmentId));
    }
  });
  (indent.recommendations || []).forEach(collect);
  if (
    indent.assignment &&
    !indent.assignment.equipment &&
    indent.assignment.equipmentId != null &&
    indent.assignment.userEquipmentId == null
  ) {
    ids.add(Number(indent.assignment.equipmentId));
  }
  if (ids.size === 0) return indent;

  const entries = await Promise.all(
    [...ids].map(async (equipmentId) => {
      try {
        const equipment = await getEquipment(equipmentId);
        return [equipmentId, equipment];
      } catch {
        return [equipmentId, null];
      }
    }),
  );
  const byId = Object.fromEntries(entries);

  const patchRec = (r) => {
    if (r.userEquipmentId != null || r.targetType === 'user_equipment') return r;
    const key = Number(r.equipmentId);
    if (r.equipment || r.equipmentId == null || !byId[key]) return r;
    const equipment = byId[key];
    return {
      ...r,
      equipment,
      assetId: equipment.assetId || r.assetId,
      assetCode: equipment.assetId || r.assetCode,
      assetName:
        equipment.equipmentDescription || equipment.equipmentType || r.assetName,
      equipmentName:
        equipment.equipmentDescription || equipment.equipmentType || r.equipmentName,
      equipmentType: equipment.equipmentType || r.equipmentType,
      regNo: equipment.regNo || r.regNo,
      manufacturer: equipment.manufacturer || r.manufacturer,
      model: equipment.model || r.model,
    };
  };

  const patchAssignment = (assignment) => {
    if (
      !assignment ||
      assignment.equipment ||
      assignment.userEquipmentId != null ||
      assignment.equipmentId == null
    ) {
      return assignment;
    }
    const equipment = byId[Number(assignment.equipmentId)];
    if (!equipment) return assignment;
    return {
      ...assignment,
      equipment,
      assetId: equipment.assetId || assignment.assetId,
    };
  };

  return {
    ...indent,
    lines: (indent.lines || []).map((line) => ({
      ...line,
      recommendations: (line.recommendations || []).map(patchRec),
      assignment: patchAssignment(line.assignment),
    })),
    recommendations: (indent.recommendations || []).map(patchRec),
    assignment: patchAssignment(indent.assignment),
  };
}

export async function createIndent(ui) {
  if (!USE_API) throw new Error('Create indent requires API mode');
  try {
    return indentFromApi(await indentsApi.create(indentToApi(ui)));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to create indent'));
  }
}

export async function recommendIndentLine(requestId, lineId, body) {
  if (!USE_API) throw new Error('Recommend requires API mode');
  try {
    return await enrichIndentEquipment(
      indentFromApi(await indentsApi.recommend(requestId, lineId, body)),
    );
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to recommend'));
  }
}

/** @deprecated Prefer recommendIndentLine(requestId, lineId, body) */
export async function recommendIndent(requestId, body) {
  const lineId = body?.lineId;
  if (lineId == null) {
    throw new Error('lineId is required for recommend');
  }
  const { lineId: _ignored, ...rest } = body;
  return recommendIndentLine(requestId, lineId, rest);
}

export async function removeIndentRecommendation(requestId, lineId, recId) {
  if (!USE_API) throw new Error('Remove recommendation requires API mode');
  try {
    return await enrichIndentEquipment(
      indentFromApi(
        await indentsApi.removeRecommendation(requestId, lineId, recId),
      ),
    );
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to remove recommendation'));
  }
}

export async function assignIndentLine(requestId, lineId, body) {
  if (!USE_API) throw new Error('Assign requires API mode');
  try {
    return await enrichIndentEquipment(
      indentFromApi(await indentsApi.assign(requestId, lineId, body)),
    );
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to assign'));
  }
}

export async function updateIndentAssignment(requestId, lineId, body) {
  if (!USE_API) throw new Error('Update assignment requires API mode');
  try {
    return await enrichIndentEquipment(
      indentFromApi(
        await indentsApi.updateAssignment(requestId, lineId, body),
      ),
    );
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to update assignment'));
  }
}

/** @deprecated Prefer assignIndentLine */
export async function assignIndent(requestId, body) {
  const lineId = body?.lineId;
  if (lineId == null) throw new Error('lineId is required for assign');
  const { lineId: _ignored, ...rest } = body;
  return assignIndentLine(requestId, lineId, rest);
}

export async function releaseIndentLine(requestId, lineId, body) {
  if (!USE_API) throw new Error('Release requires API mode');
  try {
    return await enrichIndentEquipment(
      indentFromApi(await indentsApi.release(requestId, lineId, body)),
    );
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to release'));
  }
}

/** @deprecated Prefer releaseIndentLine */
export async function releaseIndent(requestId, body) {
  const lineId = body?.lineId;
  if (lineId == null) throw new Error('lineId is required for release');
  const { lineId: _ignored, ...rest } = body;
  return releaseIndentLine(requestId, lineId, rest);
}

export async function cancelIndent(id) {
  if (!USE_API) throw new Error('Cancel requires API mode');
  try {
    return indentFromApi(await indentsApi.cancel(id));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to cancel indent'));
  }
}

export async function deleteIndent(id) {
  if (!USE_API) throw new Error('Delete indent requires API mode');
  try {
    await indentsApi.remove(id);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to delete indent'));
  }
}
